/**
 * FM-01 Scaffolded architecture (INV-CH-FM-01, ENGINE-SPEC §4; change C-FM01-REG).
 *
 * Proportion channel: `composite = registerShare·0.75 + imperRate·0.6`, the seed's formula, with
 * the lines coming from the document model instead of the seed's line heuristics:
 *   - structural lines: blocks of the structural register that are text lines (headings, list
 *     items, table cells; one line each). Scene breaks, front matter, footnote definitions and raw
 *     HTML blocks are not text lines and are excluded from numerator and denominator alike;
 *   - the seed's label-line rule, applied to prose lines: a short colon-terminated line
 *     ("Frequency:") or a "Label: text" line whose label has at most 6 words ("Zone 1: …");
 *   - registerShare = (structural + label lines) / lines (prose and structural registers).
 *   - imperRate = prescriptive sentence openings ("Do not…", "Use…", "You must…") / prose sentences;
 *     a label prefix is peeled before the test, as the seed peeled it from its prose.
 *
 * Estimator: K/E with E = lines. Each structural or label line is an event of weight 0.75; each
 * imperative is an event of weight `0.6·L/S` (L lines, S prose sentences), so that Σk/ΣE equals
 * the composite exactly. If the composite exceeds 1 (a binomial cannot), all k are scaled to K = E.
 *
 * D2: the ramp gets the zero guard (0, 10) (the seed's minimum). The seed's confidence formula
 * (`clamp(lines/12, .25, .8)`) is replaced by the framework's κ.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseSentenceList } from './helpers.ts';
import type { ChannelDef, Measurement, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[0, 10],
	[0.02, 10],
	[0.08, 26],
	[0.18, 46],
	[0.3, 64],
	[0.45, 80],
	[0.6, 92],
];

const W_LINE = 0.75;
const W_IMPER = 0.6;
const F_STRUCT = 1;
const F_LABEL = 2;
const F_IMPER = 3;

/** Structural block kinds that are text lines of the scaffold. */
const LINE_KINDS = new Set(['heading', 'listItem', 'tableCell']);

export const fm01Def: ChannelDef = {
	id: 'FM-01',
	name: 'Scaffolded architecture',
	family: 'CHOREO',
	axis: 'authorship',
	registers: ['prose', 'structural'],
	direction: 1,
	oneWay: true,
	estimator: 'proportion',
	features: [
		{ id: 'composite', label: 'Register share ×0.75 + imperative rate ×0.6' },
		{ id: 'structuralLine', label: 'Structural lines (headings, list items, table cells)' },
		{ id: 'labelLine', label: 'Colon-led labels ("Frequency:", "Zone 1: …")' },
		{ id: 'imperative', label: 'Prescriptive sentence openings' },
	],
	minExposure: 4,
	exposureUnit: 'lines',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'composite',
	prior: derivePrior(RAMP, 1, 'lines', 1.5),
	weight: 1,
	nd: 0.15,
	changes: ['C-FM01-REG'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

const LABEL = /^([A-Z][\w\s&/'’.-]{0,44}):\s+(\S.*)$/;
const NUMBERED_HEAD = /^\d+(?:\s*[&,]\s*\d+)*[.)]\s/;

const TERMINAL_END = /[.!?…:]["”’')\]]*$/;
/** A colon-led label line: a short line ending in ':' (the seed's rule). */
const isLabelTerminal = (line: string): boolean =>
	line.trim().endsWith(':') && line.trim().split(/\s+/).length <= 12;
const isLabelStart = (line: string): boolean => {
	const m = LABEL.exec(line.trim());
	return m !== null && (m[1] as string).split(/\s+/).length <= 6;
};

/**
 * The LOGICAL lines of a prose block (ENGINE-SPEC §2.4, §4 FM-01): the seed counted every physical `\n` line, but in a
 * hard-wrapped text (a txt export, a PDF, Markdown with soft breaks) a `\n` is a soft wrap, and counting wrapped lines
 * makes the exposure a function of the page width (a plain 2,600-word text has 36 paragraphs and 176 PDF lines).
 * A physical line starts a new logical line only where there is evidence of a real line: the previous line is a
 * colon-led label line, the new line starts a "Label: text" line, or the previous line is not a full-width line, ends a
 * sentence and the next starts a capital or digit. "Full-width" means at least 70% of the block's longest line. The
 * returned text keeps the block's offsets (`\n` inside a logical line becomes a space).
 */
export function logicalLines(text: string): Array<{ start: number; text: string }> {
	if (!text.includes('\n')) return [{ start: 0, text }];
	const phys: Array<{ start: number; end: number }> = [];
	let pos = 0;
	for (const raw of text.split('\n')) {
		if (raw.trim() !== '') phys.push({ start: pos, end: pos + raw.length });
		pos += raw.length + 1;
	}
	let maxLen = 0;
	for (const l of phys) maxLen = Math.max(maxLen, text.slice(l.start, l.end).trim().length);
	const out: Array<{ start: number; text: string }> = [];
	let cur: { start: number; end: number } | undefined;
	let prevText = '';
	for (const l of phys) {
		const t = text.slice(l.start, l.end);
		const tt = t.trim();
		if (cur) {
			const full = prevText.length >= 0.7 * maxLen;
			const real =
				(!full && isLabelTerminal(prevText)) ||
				isLabelStart(tt) ||
				(!full && TERMINAL_END.test(prevText) && /^["“‘'(\[]*[\p{Lu}\d]/u.test(tt));
			if (real) {
				out.push({ start: cur.start, text: text.slice(cur.start, cur.end).replace(/\n/g, ' ') });
				cur = { start: l.start, end: l.end };
			} else cur.end = l.end;
		} else cur = { start: l.start, end: l.end };
		prevText = tt;
	}
	if (cur) out.push({ start: cur.start, text: text.slice(cur.start, cur.end).replace(/\n/g, ' ') });
	return out;
}

interface Ev {
	start: number;
	end: number;
	feature: number;
	para: number;
	weight: number;
}

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-fm01'];
	const reImper = nr.get('imperativeOpener');
	const reNeed = nr.get('secondPersonNecessity');
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const events: Ev[] = [];
	// absolute char index of a label line's first character → absolute index of its remainder
	const labelRemainder = new Map<number, number>();
	let structural = 0;
	let labels = 0;
	let numbered = 0;

	for (const b of doc.blocks) {
		const p = b.para;
		if (p < 0 || b.charEnd <= b.charStart || b.sceneBreak) continue;
		if (b.register === 'structural') {
			if (!LINE_KINDS.has(b.kind)) continue;
			const text = doc.text.slice(b.charStart, b.charEnd);
			if (text.trim() === '') continue;
			e[p] = (e[p] as number) + 1;
			k[p] = (k[p] as number) + W_LINE;
			events.push({
				start: b.charStart,
				end: b.charEnd,
				feature: F_STRUCT,
				para: p,
				weight: W_LINE,
			});
			structural++;
			if (NUMBERED_HEAD.test(text.trim())) numbered++;
			continue;
		}
		if (b.register !== 'prose') continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		for (const ll of logicalLines(text)) {
			const raw = ll.text;
			const line = raw.trim();
			const lineStart = b.charStart + ll.start + (raw.length - raw.trimStart().length);
			if (line === '') continue;
			e[p] = (e[p] as number) + 1;
			const words = line.split(/\s+/).length;
			if (NUMBERED_HEAD.test(line) && words <= 14 && !/[.!?…]$/.test(line)) numbered++;
			let labelEnd = -1;
			if (words <= 12 && line.endsWith(':')) {
				labelEnd = lineStart + line.length;
			} else {
				const m = LABEL.exec(line);
				if (m && (m[1] as string).split(/\s+/).length <= 6) {
					labelEnd = lineStart + (m[1] as string).length + 1;
					labelRemainder.set(
						lineStart,
						lineStart + line.indexOf(m[2] as string, (m[1] as string).length)
					);
				}
			}
			if (labelEnd >= 0) {
				k[p] = (k[p] as number) + W_LINE;
				events.push({
					start: lineStart,
					end: labelEnd,
					feature: F_LABEL,
					para: p,
					weight: W_LINE,
				});
				labels++;
			}
		}
	}

	// prescriptive openings among prose sentences (one-word sentences dropped as in the seed)
	const pl = proseSentenceList(doc);
	const S = doc.sentences;
	let sc = 0;
	// bench measurement (BENCH-V2-SPEC §5.1): the imperative weight 0.6·L/S and the final scale are document-level; with a
	// frozen context they are left to DocState, which recomputes them from these per-paragraph counts
	const nSent = new Float64Array(P);
	const nImper = new Float64Array(P);
	const kImper = new Float64Array(P);
	const imperSentences: Array<{ start: number; end: number; para: number }> = [];
	for (let i = 0; i < pl.count; i++) {
		const s = pl.sent[i] as number;
		const a = S.charStart[s] as number;
		const en = S.charEnd[s] as number;
		let text = doc.text.slice(a, en);
		if (!/\S\s+\S/.test(text)) continue;
		sc++;
		nSent[pl.para[i] as number] = (nSent[pl.para[i] as number] as number) + 1;
		const rem = labelRemainder.get(a);
		if (rem !== undefined) text = doc.text.slice(rem, en);
		if (reImper.test(text) || reNeed.test(text))
			imperSentences.push({ start: a, end: en, para: pl.para[i] as number });
	}
	let L = 0;
	for (let p = 0; p < P; p++) L += e[p] as number;
	const imper = imperSentences.length;
	const frozen = ctx.frozen?.channels['FM-01']?.features;
	const wImper = frozen
		? (frozen.sentences ?? 0) > 0
			? (W_IMPER * (frozen.lines ?? 0)) / (frozen.sentences as number)
			: 0
		: sc > 0
			? (W_IMPER * L) / sc
			: 0;
	for (const s of imperSentences) {
		nImper[s.para] = (nImper[s.para] as number) + 1;
		if (!ctx.frozen) {
			k[s.para] = (k[s.para] as number) + wImper;
			kImper[s.para] = (kImper[s.para] as number) + wImper;
		}
		events.push({ start: s.start, end: s.end, feature: F_IMPER, para: s.para, weight: wImper });
	}
	let K = 0;
	for (let p = 0; p < P; p++) K += k[p] as number;
	const scale = !ctx.frozen && K > L && K > 0 ? L / K : 1;
	if (scale < 1) for (let p = 0; p < P; p++) k[p] = (k[p] as number) * scale;

	const ev = makeEvidence();
	for (const x of events) ev.add(x.start, x.end, x.feature, x.weight * scale);

	const registerShare = L > 0 ? (structural + labels) / L : 0;
	const imperRate = sc > 0 ? imper / sc : 0;
	const summary: string[] = [];
	if (structural) summary.push(`${structural} structural lines: headers / standalone fragments`);
	if (labels) summary.push(`${labels} colon-led labels: "Zone 1:", "Frequency:" style`);
	if (numbered) summary.push(`${numbered} numbered section heads: listicle segmentation`);
	if (imper) summary.push(`${imper} prescriptive openings: imperatives / "You must"`);
	summary.push(
		`register ratio ${fmt(registerShare, 2)} · imperative rate ${fmt(imperRate * 100, 0)}%`
	);
	return {
		k,
		e,
		evidence: ev,
		notes: scale < 1 ? ['composite above 1: events scaled to the number of lines'] : [],
		aux: { nSent, nImper, kDocLevel: kImper, scale },
		features: {
			registerShare,
			imperativeRate: imperRate,
			structuralLines: structural,
			labelLines: labels,
			numberedHeads: numbered,
			imperatives: imper,
			lines: L,
			sentences: sc,
		},
		summary,
	};
}

export const fm01 = rateChannel(fm01Def, measure);
export { fm01Def as def, fm01 as impl };
